import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type { Location, ShopifyPickupOptions, UpdateLocationPayload, ZoneType } from "../../api/types";
import { useDismissOnEscape } from "../../hooks/useDismissOnEscape";
import { closedDateLabel, hoursLines } from "../../utils/openingHours";
import { ReasonModal } from "../ui/ReasonModal";
import { Select } from "../ui/Select";
import { HoursEditor } from "./HoursEditor";
import { openPill } from "./shopLabels";

const ZONE_LABELS: Record<ZoneType, string> = {
  BOX: "Box",
  FRIDGE: "Fridge",
  DRY_STORAGE: "Dry storage",
  FRONT_SHOP: "Front shop",
  CUSTOM: "Custom",
};
const ZONE_OPTIONS: ZoneType[] = ["FRIDGE", "DRY_STORAGE", "FRONT_SHOP", "CUSTOM"];

type Editing = "hours" | "contact" | "pin" | "zone" | null;

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="shop-section" aria-label={title}>
      <div className="shop-section-head">
        <h3>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function EditLink({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className="button plain shop-section-action" onClick={onClick} disabled={disabled}>
      {label}
    </button>
  );
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/**
 * G-203 redesign — one shop, everything the owner does to it, in the house
 * panel (a bottom sheet on a phone, docked on the right from 1001 px, as the
 * Team page's). Each section edits itself in place and saves on its own, so a
 * holiday closure is one tap and one save; nothing here is a modal except the
 * deactivate confirmation (`ReasonModal`, G-106: the panel stays under it).
 *
 * `location === null` is the create form: the same panel, empty.
 */
export function ShopSheet({
  location,
  pickup,
  now,
  today,
  onClose,
  onChanged,
  onCreated,
}: {
  location: Location | null;
  pickup: ShopifyPickupOptions | null;
  now: Date;
  today: string;
  onClose: () => void;
  /** After any write: reload the list; the panel stays on the same shop. */
  onChanged: () => Promise<void>;
  onCreated: (created: Location) => Promise<void>;
}) {
  const [editing, setEditing] = useState<Editing>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmActive, setConfirmActive] = useState<boolean | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  useDismissOnEscape(() => {
    if (confirmActive !== null) return;
    onClose();
  });

  // G-106: inert while the confirmation covers it.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    rootRef.current?.toggleAttribute("inert", confirmActive !== null);
  }, [confirmActive]);

  // A new shop in the panel: fresh section state.
  useEffect(() => {
    setEditing(null);
    setError(null);
    setSavedNote(null);
  }, [location?.id]);

  useEffect(() => {
    if (!savedNote) return;
    const t = window.setTimeout(() => setSavedNote(null), 2500);
    return () => window.clearTimeout(t);
  }, [savedNote]);

  const save = async (payload: UpdateLocationPayload, note: string) => {
    if (!location) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateLocation(location.id, payload);
      await onChanged();
      setEditing(null);
      setSavedNote(note);
    } catch (err: unknown) {
      setError(errorText(err, "Could not save."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={rootRef}
      className="inv-sheet-backdrop shop-sheet-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="inv-sheet shop-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={location ? location.name : "New location"}
      >
        {location ? (
          <>
            <div className="shop-sheet-head">
              <div className="shop-sheet-who">
                <h2 className="shop-sheet-name">{location.name}</h2>
                <div className="muted shop-sheet-address">{location.address}</div>
                <div className="shop-tile-pills">
                  {location.isActive ? (
                    <span className={`shop-pill is-${openPill(location, now).tone}`}>{openPill(location, now).label}</span>
                  ) : (
                    <span className="shop-pill is-muted">Inactive</span>
                  )}
                  <span className="shop-pill is-muted">{location.code}</span>
                </div>
              </div>
              <button type="button" className="icon-button shop-sheet-close" onClick={onClose} aria-label="Close">
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M2 2l10 10M12 2L2 12" /></svg>
              </button>
            </div>
            {savedNote ? <p className="shop-saved" role="status">{savedNote}</p> : null}

            <Section
              title="Hours"
              action={editing === "hours" ? undefined : <EditLink label="Edit" onClick={() => setEditing("hours")} disabled={busy} />}
            >
              {editing === "hours" ? (
                <HoursEditor
                  stored={location.openingHours}
                  busy={busy}
                  error={error}
                  onSave={(next) => void save({ openingHours: next }, "Hours saved")}
                  onCancel={() => { setEditing(null); setError(null); }}
                />
              ) : location.openingHours.length === 0 ? (
                <p className="muted shop-empty">Hours not set</p>
              ) : (
                <dl className="shop-rows">
                  {hoursLines(location.openingHours).map((l) => (
                    <div key={l.days} className="shop-row">
                      <dt>{l.days}</dt>
                      <dd className={l.closed ? "muted" : undefined}>{l.text}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </Section>

            <ClosedDatesSection
              location={location}
              today={today}
              busy={busy}
              error={editing === null ? error : null}
              onSave={(dates, note) => void save({ closedDates: dates }, note)}
            />

            <Section
              title="Contact & ticket"
              action={editing === "contact" ? undefined : <EditLink label="Edit" onClick={() => setEditing("contact")} disabled={busy} />}
            >
              {editing === "contact" ? (
                <ContactForm
                  location={location}
                  busy={busy}
                  error={error}
                  onSave={(payload) => void save(payload, "Contact saved")}
                  onCancel={() => { setEditing(null); setError(null); }}
                />
              ) : (
                <dl className="shop-rows">
                  <div className="shop-row"><dt>Phone</dt><dd>{location.phone ?? <span className="muted">Not set</span>}</dd></div>
                  <div className="shop-row"><dt>E-mail</dt><dd className="shop-wrap">{location.publicEmail ?? <span className="muted">Not set</span>}</dd></div>
                  <div className="shop-row"><dt>NIF</dt><dd>{location.nif ? <>{location.nif} <span className="muted">· on the ticket</span></> : <span className="muted">Not on the ticket</span>}</dd></div>
                </dl>
              )}
            </Section>

            <Section
              title="Door pin"
              action={editing === "pin" ? undefined : <EditLink label={location.lat != null ? "Change" : "Set"} onClick={() => setEditing("pin")} disabled={busy} />}
            >
              {editing === "pin" ? (
                <PinForm
                  location={location}
                  busy={busy}
                  error={error}
                  onSave={(payload) => void save(payload, "Door pin saved")}
                  onCancel={() => { setEditing(null); setError(null); }}
                />
              ) : (
                <div className="shop-pin">
                  {location.lat != null && location.lng != null ? (
                    <>
                      <span className="shop-pill is-success">Pinned</span>
                      <span className="muted">{location.lat}, {location.lng} · couriers confirm arrival within 50 m</span>
                    </>
                  ) : (
                    <>
                      <span className="shop-pill is-warning">No door pin</span>
                      <span className="muted">Couriers cannot confirm arrival; the tablet arms as “unknown”</span>
                    </>
                  )}
                  {location.mapsUrl ? (
                    <a className="shop-maps-link" href={location.mapsUrl} target="_blank" rel="noreferrer">
                      Open in Google Maps
                    </a>
                  ) : (
                    <span className="muted">No Google Maps link</span>
                  )}
                </div>
              )}
            </Section>

            <ZonesSection
              location={location}
              editing={editing === "zone"}
              busy={busy}
              error={editing === "zone" ? error : null}
              onStart={() => setEditing("zone")}
              onStop={() => { setEditing(null); setError(null); }}
              onBusy={setBusy}
              onError={setError}
              onChanged={async () => { await onChanged(); setSavedNote("Zones updated"); }}
            />

            <Section title="Shopify pickup">
              {pickup?.connected ? (
                <label className="field shop-field">
                  <span>Collected at</span>
                  <Select
                    aria-label={`Shopify pickup location for ${location.name}`}
                    value={location.shopifyStoreDomain === pickup.storeDomain ? location.shopifyLocationId ?? "" : ""}
                    disabled={!location.isActive || busy}
                    onChange={(event) => {
                      const value = event.target.value || null;
                      setBusy(true);
                      setError(null);
                      void api
                        .mapShopifyPickupLocation(location.id, value)
                        .then(() => onChanged())
                        .then(() => setSavedNote("Shopify pickup saved"))
                        .catch((err: unknown) => setError(errorText(err, "Could not save the pickup mapping.")))
                        .finally(() => setBusy(false));
                    }}
                  >
                    <option value="">Not mapped</option>
                    {pickup.locations.map((option) => (
                      <option
                        key={option.id}
                        value={option.id}
                        disabled={option.mappedLocation !== null && option.mappedLocation.id !== location.id}
                      >
                        {option.name}
                        {option.address ? ` · ${option.address}` : ""}
                        {option.mappedLocation && option.mappedLocation.id !== location.id ? ` (mapped to ${option.mappedLocation.name})` : ""}
                      </option>
                    ))}
                  </Select>
                </label>
              ) : (
                <p className="muted shop-empty">Connect Shopify first</p>
              )}
              {editing === null && error ? <p className="shop-section-error">{error}</p> : null}
            </Section>

            <section className="shop-section shop-section-foot" aria-label="Shop status">
              <Link className="button plain" to={`/app/inventory?locationId=${encodeURIComponent(location.id)}`}>
                Open inventory
              </Link>
              {location.isActive ? (
                <button type="button" className="button plain is-danger" disabled={busy} onClick={() => setConfirmActive(false)}>
                  Deactivate
                </button>
              ) : (
                <button type="button" className="button tinted" disabled={busy} onClick={() => setConfirmActive(true)}>
                  Reactivate
                </button>
              )}
            </section>
          </>
        ) : (
          <CreateForm onClose={onClose} onCreated={onCreated} />
        )}
      </div>

      {location && confirmActive !== null ? (
        <ReasonModal
          title={confirmActive ? `Reactivate ${location.name}?` : `Deactivate ${location.name}?`}
          message={
            confirmActive
              ? "The shop returns to every shop list and the tablet can be armed for it again."
              : "The shop leaves every shop list. Stock must be moved or written off and staff reassigned first; the app refuses otherwise."
          }
          reasonMode="none"
          confirmLabel={confirmActive ? "Reactivate" : "Deactivate"}
          destructive={!confirmActive}
          busy={busy}
          error={error}
          onConfirm={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.updateLocation(location.id, { isActive: confirmActive });
              await onChanged();
              setConfirmActive(null);
              setSavedNote(confirmActive ? "Shop reactivated" : "Shop deactivated");
            } catch (err: unknown) {
              setError(errorText(err, "Could not change the shop."));
            } finally {
              setBusy(false);
            }
          }}
          onClose={() => { setConfirmActive(null); setError(null); }}
        />
      ) : null}
    </div>
  );
}

function ClosedDatesSection({
  location,
  today,
  busy,
  error,
  onSave,
}: {
  location: Location;
  today: string;
  busy: boolean;
  error: string | null;
  onSave: (dates: Array<{ date: string; note?: string }>, note: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const upcoming = location.closedDates.filter((d) => d.date >= today);
  const past = location.closedDates.length - upcoming.length;

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!date) return;
    if (location.closedDates.some((d) => d.date === date)) {
      setLocalError(`${closedDateLabel(date, today)} is already in the list.`);
      return;
    }
    setLocalError(null);
    onSave(
      [...location.closedDates.map((d) => ({ date: d.date, note: d.note ?? undefined })), { date, note: note.trim() || undefined }],
      "Closed date added",
    );
    setDate("");
    setNote("");
    setAdding(false);
  };

  const remove = (which: string) =>
    onSave(
      location.closedDates.filter((d) => d.date !== which).map((d) => ({ date: d.date, note: d.note ?? undefined })),
      "Closed date removed",
    );

  return (
    <Section
      title="Closed dates"
      action={adding ? undefined : <EditLink label="Add a date" onClick={() => setAdding(true)} disabled={busy} />}
    >
      {upcoming.length === 0 && !adding ? <p className="muted shop-empty">No closed dates ahead</p> : null}
      {upcoming.length > 0 ? (
        <ul className="shop-dates">
          {upcoming.map((d) => (
            <li key={d.date} className="shop-date">
              <span>
                {closedDateLabel(d.date, today)}
                {d.note ? <span className="muted"> · {d.note}</span> : null}
              </span>
              <button type="button" className="button plain shop-section-action" disabled={busy} aria-label={`Remove ${d.date}`} onClick={() => remove(d.date)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {past > 0 ? <p className="muted shop-empty">{past} past {past === 1 ? "date" : "dates"} kept</p> : null}
      {adding ? (
        <form className="shop-date-add" onSubmit={add}>
          <input type="date" aria-label="Closed date" value={date} min={today} disabled={busy} onChange={(e) => setDate(e.target.value)} />
          <input type="text" aria-label="Note" placeholder="Note (optional)" maxLength={120} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
          <div className="button-row">
            <button type="submit" className="button" disabled={busy || !date}>{busy ? "Saving…" : "Add"}</button>
            <button type="button" className="button plain" disabled={busy} onClick={() => { setAdding(false); setLocalError(null); }}>Cancel</button>
          </div>
        </form>
      ) : null}
      {localError ?? error ? <p className="shop-section-error">{localError ?? error}</p> : null}
    </Section>
  );
}

function ContactForm({
  location,
  busy,
  error,
  onSave,
  onCancel,
}: {
  location: Location;
  busy: boolean;
  error: string | null;
  onSave: (payload: UpdateLocationPayload) => void;
  onCancel: () => void;
}) {
  const [phone, setPhone] = useState(location.phone ?? "");
  const [publicEmail, setPublicEmail] = useState(location.publicEmail ?? "");
  const [nif, setNif] = useState(location.nif ?? "");
  return (
    <form
      className="shop-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ phone: phone.trim(), publicEmail: publicEmail.trim(), nif: nif.trim() });
      }}
    >
      <label className="field shop-field"><span className="field-caption">Phone</span><input type="tel" maxLength={40} value={phone} disabled={busy} onChange={(e) => setPhone(e.target.value)} /></label>
      <label className="field shop-field"><span className="field-caption">E-mail</span><input type="email" maxLength={200} value={publicEmail} disabled={busy} onChange={(e) => setPublicEmail(e.target.value)} /></label>
      <label className="field shop-field"><span className="field-caption">NIF</span><input type="text" maxLength={20} value={nif} disabled={busy} onChange={(e) => setNif(e.target.value)} /></label>
      {error ? <p className="shop-section-error">{error}</p> : null}
      <div className="button-row">
        <button type="submit" className="button" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        <button type="button" className="button plain" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function PinForm({
  location,
  busy,
  error,
  onSave,
  onCancel,
}: {
  location: Location;
  busy: boolean;
  error: string | null;
  onSave: (payload: UpdateLocationPayload) => void;
  onCancel: () => void;
}) {
  const [address, setAddress] = useState(location.address);
  const [lat, setLat] = useState(location.lat ?? "");
  const [lng, setLng] = useState(location.lng ?? "");
  const [mapsUrl, setMapsUrl] = useState(location.mapsUrl ?? "");
  const hasPin = lat.trim() !== "" && lng.trim() !== "";
  return (
    <form
      className="shop-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          address: address.trim() !== location.address ? address.trim() : undefined,
          lat: hasPin ? Number(lat) : undefined,
          lng: hasPin ? Number(lng) : undefined,
          mapsUrl: mapsUrl.trim(),
        });
      }}
    >
      <label className="field shop-field"><span className="field-caption">Address</span><input type="text" maxLength={300} value={address} disabled={busy} onChange={(e) => setAddress(e.target.value)} /></label>
      <label className="field shop-field"><span className="field-caption">Google Maps link</span><input type="url" inputMode="url" maxLength={500} placeholder="https://maps.app.goo.gl/…" value={mapsUrl} disabled={busy} onChange={(e) => setMapsUrl(e.target.value)} /></label>
      <div className="shop-form-pair">
        <label className="field shop-field"><span className="field-caption">Latitude</span><input type="text" inputMode="decimal" value={lat} disabled={busy} onChange={(e) => setLat(e.target.value)} /></label>
        <label className="field shop-field"><span className="field-caption">Longitude</span><input type="text" inputMode="decimal" value={lng} disabled={busy} onChange={(e) => setLng(e.target.value)} /></label>
      </div>
      {error ? <p className="shop-section-error">{error}</p> : null}
      <div className="button-row">
        <button type="submit" className="button" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        <button type="button" className="button plain" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function ZonesSection({
  location,
  editing,
  busy,
  error,
  onStart,
  onStop,
  onBusy,
  onError,
  onChanged,
}: {
  location: Location;
  editing: boolean;
  busy: boolean;
  error: string | null;
  onStart: () => void;
  onStop: () => void;
  onBusy: (b: boolean) => void;
  onError: (e: string | null) => void;
  onChanged: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [zoneType, setZoneType] = useState<ZoneType>("CUSTOM");
  const zones = [...location.zones].sort((a, b) => a.name.localeCompare(b.name));

  const run = async (work: () => Promise<unknown>, fallback: string, after?: () => void) => {
    onBusy(true);
    onError(null);
    try {
      await work();
      await onChanged();
      after?.();
    } catch (err: unknown) {
      onError(errorText(err, fallback));
    } finally {
      onBusy(false);
    }
  };

  return (
    <Section title="Zones" action={editing ? undefined : <EditLink label="Add zone" onClick={onStart} disabled={busy} />}>
      {zones.length === 0 ? <p className="muted shop-empty">No zones yet</p> : null}
      {zones.length > 0 ? (
        <ul className="shop-zones">
          {zones.map((z) => (
            <li key={z.id} className={`shop-zone${z.isActive ? "" : " is-inactive"}`}>
              <span>
                {z.name}
                {z.name.toLowerCase() !== ZONE_LABELS[z.zoneType].toLowerCase() ? (
                  <span className="muted"> · {ZONE_LABELS[z.zoneType]}</span>
                ) : null}
              </span>
              <button
                type="button"
                className="button plain shop-section-action"
                disabled={busy}
                onClick={() => void run(() => api.updateZone(z.id, { isActive: !z.isActive }), "Could not update the zone.")}
              >
                {z.isActive ? "Deactivate" : "Reactivate"}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {editing ? (
        <form
          className="shop-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(
              () => api.createZone(location.id, { name: name.trim(), zoneType }),
              "Could not add the zone.",
              () => { setName(""); setZoneType("CUSTOM"); onStop(); },
            );
          }}
        >
          <label className="field shop-field"><span className="field-caption">New zone name</span><input type="text" maxLength={80} value={name} disabled={busy} placeholder="e.g. Walk-in cooler" onChange={(e) => setName(e.target.value)} /></label>
          <label className="field shop-field"><span className="field-caption">Type</span>
            <Select value={zoneType} disabled={busy} onChange={(e) => setZoneType(e.target.value as ZoneType)}>
              {ZONE_OPTIONS.map((zt) => <option key={zt} value={zt}>{ZONE_LABELS[zt]}</option>)}
            </Select>
          </label>
          <div className="button-row">
            <button type="submit" className="button" disabled={busy || name.trim().length === 0}>{busy ? "Adding…" : "Add zone"}</button>
            <button type="button" className="button plain" disabled={busy} onClick={onStop}>Cancel</button>
          </div>
        </form>
      ) : null}
      {error ? <p className="shop-section-error">{error}</p> : null}
    </Section>
  );
}

function CreateForm({ onClose, onCreated }: { onClose: () => void; onCreated: (created: Location) => Promise<void> }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canCreate = name.trim().length > 0 && address.trim().length > 0;
  return (
    <>
      <div className="shop-sheet-head">
        <div className="shop-sheet-who">
          <h2 className="shop-sheet-name">New location</h2>
          <div className="muted shop-sheet-address">Name and code cannot be changed later</div>
        </div>
        <button type="button" className="icon-button shop-sheet-close" onClick={onClose} aria-label="Close">
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M2 2l10 10M12 2L2 12" /></svg>
        </button>
      </div>
      <form
        className="shop-section shop-form"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          void api
            .createLocation({ name: name.trim(), address: address.trim() })
            .then((created) => onCreated(created))
            .catch((err: unknown) => setError(errorText(err, "Could not create the location.")))
            .finally(() => setBusy(false));
        }}
      >
        <label className="field shop-field"><span className="field-caption">Name</span><input type="text" required maxLength={120} value={name} disabled={busy} onChange={(e) => setName(e.target.value)} /></label>
        <label className="field shop-field"><span className="field-caption">Address</span><input type="text" required maxLength={240} value={address} disabled={busy} onChange={(e) => setAddress(e.target.value)} /></label>
        {error ? <p className="shop-section-error">{error}</p> : null}
        <div className="button-row">
          <button type="submit" className="button" disabled={busy || !canCreate}>{busy ? "Creating…" : "Create location"}</button>
          <button type="button" className="button plain" disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </>
  );
}
